import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { CHART_COLORS, inferFormat, formatValue } from '../charts/chartUtils';
import { CopyImageButton } from './CopyImageButton';
import { LocalStorage } from '../../utils/LocalStorage';

/**
 * Calcula el total/promedio de una métrica en las filas de la tabla.
 * Si aggregation === 'AVG', calcula promedio simple. Si no, suma.
 */
function getCellNumericValue(cell) {
    if (!cell) return null;
    // Con comparación: currentValue; sin comparación: value
    const v = cell.currentValue ?? cell.value;
    return (v != null && !isNaN(v)) ? Number(v) : null;
}

function calcMetricTotal(rows, metricKey, aggregation) {
    const values = rows
        .map(row => getCellNumericValue(row[metricKey]))
        .filter(v => v !== null);

    if (values.length === 0) return null;

    if (aggregation === 'AVG') {
        return values.reduce((a, b) => a + b, 0) / values.length;
    }
    return values.reduce((a, b) => a + b, 0);
}

function calcComparisonTotal(rows, metricKey, aggregation) {
    const values = rows
        .filter(row => row[metricKey] && row[metricKey].comparisonValue != null)
        .map(row => row[metricKey].comparisonValue);

    if (values.length === 0) return null;

    if (aggregation === 'AVG') {
        return values.reduce((a, b) => a + b, 0) / values.length;
    }
    return values.reduce((a, b) => a + b, 0);
}

class DataExplorerMetricCardsComponent extends Component {
    constructor(props) {
        super(props);
        // Inicializar con las primeras 4 métricas disponibles
        this.state = {
            selectedMetrics: [null, null, null, null],
            openSelectorIndex: null,
        };
        this.handleCopy = this.handleCopy.bind(this);
        this.toggleSelector = this.toggleSelector.bind(this);
        this.selectMetric = this.selectMetric.bind(this);
        this.closeSelector = this.closeSelector.bind(this);
    }

    componentDidMount() {
        this.initMetrics();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.lastFilters !== this.props.lastFilters) {
            this.initMetrics();
        }
    }

    // `chartPrefs` explícitas (portal del cliente) ganan sobre las del navegador: ahí las
    // preferencias son parte de la vista que armó la agencia, no del historial de quien mira.
    resolveChartPrefs() {
        if (this.props.chartPrefs) return this.props.chartPrefs;
        return LocalStorage.getDataExplorerChartPreferences();
    }

    initMetrics() {
        const { lastFilters } = this.props;
        if (!lastFilters || !lastFilters.metrics) return;

        const prefs = this.resolveChartPrefs();
        if (prefs && prefs.cardMetrics) {
            const validSaved = prefs.cardMetrics.map(m =>
                m && lastFilters.metrics.includes(m) ? m : null
            );
            if (validSaved.some(m => m !== null)) {
                this.setState({ selectedMetrics: validSaved });
                return;
            }
        }

        const metrics = lastFilters.metrics.slice(0, 4);
        const selected = [null, null, null, null];
        metrics.forEach((m, i) => { selected[i] = m; });
        this.setState({ selectedMetrics: selected });
    }

    getFieldData(metricName) {
        const { fieldsData } = this.props;
        if (!fieldsData || !fieldsData.FIELD_DATA_BY_ID || !metricName) return null;
        return fieldsData.FIELD_DATA_BY_ID[metricName] || null;
    }

    getAggregation(metricName) {
        const fieldData = this.getFieldData(metricName);
        return fieldData ? fieldData.aggregation : 'SUM';
    }

    getLabel(metricName) {
        const fieldData = this.getFieldData(metricName);
        if (fieldData && fieldData.name) return fieldData.name;
        return metricName || '';
    }

    handleCopy(metricName, value) {
        const label = this.getLabel(metricName);
        const text = `${label}: ${value}`;
        navigator.clipboard.writeText(text).catch(() => {});
    }

    toggleSelector(index) {
        this.setState(prev => ({
            openSelectorIndex: prev.openSelectorIndex === index ? null : index
        }));
    }

    closeSelector() {
        this.setState({ openSelectorIndex: null });
    }

    selectMetric(index, metricName) {
        this.setState(prev => {
            const selected = [...prev.selectedMetrics];
            selected[index] = metricName;
            this.saveCardMetricsPreference(selected);
            return { selectedMetrics: selected, openSelectorIndex: null };
        });
    }

    saveCardMetricsPreference(cardMetrics) {
        if (!this.props.chartPrefs) {
            const prefs = LocalStorage.getDataExplorerChartPreferences() || {};
            LocalStorage.saveDataExplorerChartPreferences({ ...prefs, cardMetrics });
        }
        if (this.props.onChartPrefsChange) this.props.onChartPrefsChange();
    }

    getAvailableMetrics() {
        const { lastFilters } = this.props;
        if (!lastFilters || !lastFilters.metrics) return [];
        return lastFilters.metrics;
    }

    renderCard(metricName, index) {
        const { t, results } = this.props;
        const { openSelectorIndex } = this.state;
        const availableMetrics = this.getAvailableMetrics();
        const isClient = this.props.viewMode === 'client';

        if (!metricName) {
            // En la vista del cliente las métricas vienen fijadas por la vista guardada:
            // no se muestran slots vacíos editables (se omite el card fantasma).
            if (isClient) return null;
            // Mostrar selector vacío
            return (
                <div key={index} className="de-metric-card de-metric-card--empty">
                    <button
                        className="de-metric-card__add-btn btn btn-sm -negative"
                        onClick={() => this.toggleSelector(index)}
                    >
                        + {t('data_explorer.change_metric', 'Cambiar métrica')}
                    </button>
                    {openSelectorIndex === index && (
                        <div className="de-metric-card__selector">
                            {availableMetrics.map(m => (
                                <div
                                    key={m}
                                    className="de-metric-card__selector-option"
                                    onClick={() => this.selectMetric(index, m)}
                                >
                                    {this.getLabel(m)}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        const summary = results && results.summary ? results.summary : {};
        const summaryEntry = summary[metricName];
        const format = inferFormat(metricName);
        const label = this.getLabel(metricName);

        let total = null;
        let deltaPercent = null;
        let deltaPositive = null;
        let formattedValue = '-';

        if (summaryEntry) {
            // Usar summary del backend (calcula correctamente ratios como ROAS, CPA, CTR)
            total = summaryEntry.currentValue ?? summaryEntry.value ?? null;
            if (total != null) total = Number(total);
            formattedValue = summaryEntry.formattedCurrentValue || summaryEntry.formattedValue || (total !== null ? formatValue(total, format) : '-');

            if (summaryEntry.deltaPercentage != null) {
                deltaPercent = summaryEntry.deltaPercentage * 100;
                deltaPositive = deltaPercent >= 0;
            }
        } else {
            // Fallback: calcular desde filas (para métricas no incluidas en summary)
            const rows = results && results.tableData && results.tableData.rows ? results.tableData.rows : [];
            const metricKey = `metric_${metricName}`;
            const aggregation = this.getAggregation(metricName);
            total = calcMetricTotal(rows, metricKey, aggregation);
            const compTotal = calcComparisonTotal(rows, metricKey, aggregation);
            formattedValue = total !== null ? formatValue(total, format) : '-';

            if (compTotal !== null && compTotal !== 0 && total !== null) {
                deltaPercent = ((total - compTotal) / Math.abs(compTotal)) * 100;
                deltaPositive = deltaPercent >= 0;
            }
        }

        return (
            <div key={index} className="de-metric-card">
                <div className="de-metric-card__header">
                    <span className="de-metric-card__title">{label}</span>
                    <div className="de-metric-card__actions">
                        <button
                            className="de-metric-card__action-btn"
                            title={t('data_explorer.copy_value', 'Copiar')}
                            onClick={() => this.handleCopy(metricName, formattedValue)}
                        >
                            <i className="fas fa-copy"></i>
                        </button>
                        {!isClient && (
                            <button
                                className="de-metric-card__action-btn"
                                title={t('data_explorer.change_metric', 'Cambiar métrica')}
                                onClick={() => this.toggleSelector(index)}
                            >
                                <i className="fas fa-pen"></i>
                            </button>
                        )}
                    </div>
                </div>
                <div
                    className="de-metric-card__color-bar"
                    style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                />
                <div className="de-metric-card__value">{formattedValue}</div>
                {deltaPercent !== null && (
                    <span className={`de-metric-card__delta ${deltaPositive ? '-positive' : '-negative'}`}>
                        {deltaPositive ? '↑' : '↓'} {Math.abs(deltaPercent).toFixed(1)}%
                    </span>
                )}
                {!isClient && openSelectorIndex === index && (
                    <div className="de-metric-card__selector">
                        {availableMetrics.map(m => (
                            <div
                                key={m}
                                className={`de-metric-card__selector-option ${m === metricName ? '-active' : ''}`}
                                onClick={() => this.selectMetric(index, m)}
                            >
                                {this.getLabel(m)}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    render() {
        const { t } = this.props;
        const { selectedMetrics } = this.state;

        return (
            <CopyImageButton>
                <div className="de-chart-block">
                    <div className="de-chart-block-header">
                        <h3>{t('data_explorer.kpi_cards', 'Métricas clave')}</h3>
                    </div>
                    <div className="de-metric-cards" onClick={e => {
                        // Cerrar el selector si se hace click fuera de un card
                        if (!e.target.closest('.de-metric-card')) {
                            this.closeSelector();
                        }
                    }}>
                        {selectedMetrics.map((metric, i) => this.renderCard(metric, i))}
                    </div>
                </div>
            </CopyImageButton>
        );
    }
}

export const DataExplorerMetricCards = withTranslation()(DataExplorerMetricCardsComponent);
